/**
 * GolferEmailRequiredModal — hard-gate modal that captures a real email
 * address from a phone-only golfer, or prompts verification when
 * customers.email_verified is still false.
 *
 * Verification is by 6-digit OTP (no magic link), matching Core's
 * ContactVerifyModal and Fitting's CustomerRegister — Supabase emails the
 * numeric token whenever updateUser/resend runs WITHOUT emailRedirectTo.
 *
 * States:
 *   1. Enter email  — placeholder / phone-only synthetic address; on submit
 *                     we dispatch a code.
 *   2. Verify+send  — real email on file but unverified; tap Send to get a code.
 *   3. Enter code   — 6-digit input + Resend; verifyOtp(email_change) confirms,
 *                     then the confirmed address is promoted into customers.
 *
 * A code is only ever sent when updateUser/resend actually ran.
 */

import { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { needsEmailForAction } from '../../lib/emailGate';
import { isPlaceholderEmail } from '../../lib/golferEmail';
import { requestEmailOtp, verifyEmailChangeOtp } from '../../lib/requestEmailOtp';
import { reportError } from '../../lib/observability';
import Button from '../ui/Button';
import { Alert } from '../ui/Alert';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function GolferEmailRequiredModal({ open, onClose }: Props) {
  const customer = useAuthStore((s) => s.customer);
  const refreshCustomer = useAuthStore((s) => s.refreshCustomer);

  const [user, setUser] = useState<User | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Progress copy that isn't a failure — currently only the secure
  // email-change "now enter the code from the other inbox" step.
  const [notice, setNotice] = useState<string | null>(null);
  // The email we last dispatched a 6-digit code to this session (OTP flow,
  // mirrors Core's ContactVerifyModal). Drives the code-entry step.
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [syncingVerified, setSyncingVerified] = useState(false);

  useEffect(() => {
    if (!open) return;
    supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        if (event === 'USER_UPDATED') await refreshCustomer();
      }
    });
    return () => subscription.unsubscribe();
  }, [open, refreshCustomer]);

  // Reconcile customers.email from the authoritative auth user.
  //
  // A phone-signup golfer's real email lives on the Supabase auth user. Core
  // deliberately keeps the phone PLACEHOLDER in customers.email until the
  // address is verified (customers.email_lower is UNIQUE and drives
  // cross-account claiming, so it must never hold an unverified email — see
  // useAuth.ts). The old heal bailed whenever customers.email was a
  // placeholder, so once Auth confirmed the email the placeholder was never
  // replaced and needsEmailForAction (which checks placeholder first) showed
  // "Add your email" forever for an address the golfer already verified.
  //
  // Fix: when Auth has a CONFIRMED real email, promote it into customers
  // (email + email_lower + email_verified). Only confirmed addresses are
  // written, so the unique-column claim is backed by proven ownership. A
  // still-pending email-change (user.new_email, unconfirmed) is intentionally
  // NOT written — that would put an unverified address into the unique column.
  useEffect(() => {
    if (!open || !customer || syncingVerified) return;
    const authEmail =
      user?.email && !isPlaceholderEmail(user.email) ? user.email.toLowerCase() : null;
    const authConfirmed = !!(authEmail && user?.email_confirmed_at);
    if (!authConfirmed) return;

    const custEmail = (customer.email ?? '').toLowerCase();
    const needsEmailPromote = isPlaceholderEmail(customer.email) || custEmail !== authEmail;
    const needsVerifiedFlag = customer.email_verified !== true;
    if (!needsEmailPromote && !needsVerifiedFlag) return;

    let cancelled = false;
    setSyncingVerified(true);
    (async () => {
      try {
        const patch = needsEmailPromote
          ? { email: user!.email!, email_lower: authEmail!, email_verified: true }
          : { email_verified: true };
        const { error } = await supabase
          .from('customers')
          .update(patch)
          .eq('id', customer.id);
        if (error) throw error;
        if (!cancelled) await refreshCustomer();
      } catch (err) {
        // 23505 = another customers row already claims this (now-verified)
        // email — a prior Fitting invite. Cross-account merge needs
        // server-side resolution; leave state as-is rather than guess here.
        reportError(err, { scope: 'golferEmailGate.customersReconcile' });
      } finally {
        if (!cancelled) setSyncingVerified(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, customer, user, refreshCustomer, syncingVerified]);

  const authPendingEmail = (user as { new_email?: string | null })?.new_email ?? null;
  // We're in the code-entry step when we've sent a code this session, or the
  // auth user carries an outstanding (real) email-change to enter a code for.
  const pendingEmail =
    codeSentTo ??
    (authPendingEmail && !isPlaceholderEmail(authPendingEmail) ? authPendingEmail : null);
  const showOtpEntry = Boolean(pendingEmail);

  if (!open) return null;

  const gate = needsEmailForAction(customer ?? null);
  if (customer && !gate.block && !showOtpEntry) {
    return null;
  }

  // Still syncing Auth→customers confirmation — don't flash false "sent" copy.
  if (syncingVerified || (customer && gate.reason === 'unverified' && user?.email_confirmed_at)) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg max-w-md w-full p-6 text-[var(--color-on-surface)]">
          <p className="text-sm text-[var(--color-on-subtle)]">Confirming your email…</p>
        </div>
      </div>
    );
  }

  const effectiveReason = gate.reason ?? (customer ? null : 'placeholder');
  const showAddEmailForm = effectiveReason === 'placeholder' && !showOtpEntry;
  const showUnverifiedPrompt = effectiveReason === 'unverified' && !showOtpEntry;
  const showDismissButton = !showAddEmailForm || showOtpEntry;

  // Dispatch a 6-digit OTP to the target email through the shared helper
  // (mirrored in Core as src/lib/auth/requestEmailOtp.ts). It owns the
  // updateUser-vs-resend choice, the resend addressing trap, and the 429
  // wording — this modal used to try updateUser first and fall back to
  // resend on error, which is backwards for a resend and re-initiated the
  // whole change every time.
  // Land a confirmed address on the customers row and close the gate. Shared by
  // the code path and the "it was already confirmed" short-circuit.
  const promoteConfirmedEmail = async (verifiedEmail: string) => {
    const lower = verifiedEmail.toLowerCase();
    if (customer) {
      const { error: custErr } = await supabase
        .from('customers')
        .update({ email: lower, email_lower: lower, email_verified: true })
        .eq('id', customer.id);
      if (custErr) {
        // 23505 = email already claimed by a prior Fitting invite; the auth
        // change still succeeded. Log and let server-side merge sort it out.
        reportError(custErr, { scope: 'golferEmailGate.promoteAfterOtp' });
      }
    }
    const { data: u } = await supabase.auth.getUser();
    setUser(u.user);
    await refreshCustomer();
    setOtp('');
    setCodeSentTo(null);
    onClose();
  };

  const sendVerification = async (targetEmail: string, resend = false) => {
    const trimmed = targetEmail.trim().toLowerCase();
    setLoading(true);
    setError(null);
    setNotice(null);
    const sent = await requestEmailOtp(trimmed, { resend });
    if (!sent.ok) {
      setError(sent.message);
      setLoading(false);
      return;
    }
    const { data: u } = await supabase.auth.getUser();
    setUser(u.user);
    // Already a confirmed identity on this account: GoTrue will not mail a code
    // for a change to the address it already holds, so a code box here can
    // never be satisfied. Promote it and let the gate clear instead.
    if (sent.mode === 'already-confirmed') {
      await promoteConfirmedEmail(sent.email);
      setLoading(false);
      return;
    }
    setCodeSentTo(trimmed);
    setOtp('');
    setEmailInput('');
    setLoading(false);
  };

  // Confirm the code. The helper sets email_confirmed_at via
  // verifyOtp(email_change) — and tells us when only one of the two secure
  // email-change codes has been accepted, which is NOT a completed change.
  // Then we promote the now-verified address into customers so the gate
  // clears (the self-heal effect can't run once we close the modal).
  const verifyCode = async () => {
    const token = otp.trim();
    if (!/^\d{6}$/.test(token)) {
      setError('Enter the 6-digit code.');
      return;
    }
    if (!pendingEmail) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await verifyEmailChangeOtp(pendingEmail, token);
      if (result.status === 'error') {
        setError(result.message);
        return;
      }
      if (result.status === 'second-code-required') {
        setNotice(result.message);
        setOtp('');
        return;
      }

      await promoteConfirmedEmail(pendingEmail);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'That code did not match. Check it and try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendVerification(emailInput);
  };

  const title = showOtpEntry
    ? 'Enter your code'
    : showAddEmailForm
      ? 'Add your email to continue'
      : 'Verify your email';

  const description = showOtpEntry
    ? `Enter the 6-digit code we sent to ${pendingEmail}. It may take a moment to arrive.`
    : showAddEmailForm
      ? 'You signed in with your phone. We need an email so receipts, quotes, transfer alerts and other updates can actually reach you.'
      : `Confirm ${customer?.email ?? 'your email'} so we can send receipts and alerts. Tap send to receive a code.`;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4"
      role="presentation"
    >
      <div
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg max-w-md w-full p-6 text-[var(--color-on-surface)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-required-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-surface-overlay)] flex items-center justify-center">
            <Mail className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="email-required-title"
              className="text-lg font-semibold text-[var(--color-on-surface)]"
            >
              {title}
            </h3>
            <p className="text-sm text-[var(--color-on-subtle)] mt-1">{description}</p>
          </div>
          {showDismissButton && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg text-[var(--color-on-subtle)] hover:bg-[var(--color-surface-overlay)] shrink-0"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {error && (
          <div className="mb-3">
            <Alert variant="destructive">{error}</Alert>
          </div>
        )}

        {notice && (
          <div className="mb-3">
            <Alert>{notice}</Alert>
          </div>
        )}

        {showAddEmailForm && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label
                htmlFor="golfer-email-required"
                className="block text-sm font-medium mb-1"
              >
                Email address
              </label>
              <input
                id="golfer-email-required"
                type="email"
                autoComplete="email"
                autoFocus
                className="input w-full"
                placeholder="you@example.com"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setError(null);
                }}
              />
            </div>
            <Button type="submit" isLoading={loading} disabled={loading} className="w-full">
              Send code
            </Button>
          </form>
        )}

        {showUnverifiedPrompt && (
          <Button
            type="button"
            isLoading={loading}
            disabled={loading || !customer?.email}
            className="w-full"
            onClick={() => void sendVerification(customer?.email ?? '')}
          >
            Send code
          </Button>
        )}

        {showOtpEntry && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verifyCode();
            }}
            className="space-y-3"
          >
            <div>
              <label htmlFor="golfer-email-otp" className="block text-sm font-medium mb-1">
                6-digit code
              </label>
              <input
                id="golfer-email-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                className="input w-full text-center text-lg tracking-[0.5em]"
                placeholder="123456"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError(null);
                }}
              />
            </div>
            <Button
              type="submit"
              isLoading={loading}
              disabled={loading || otp.length !== 6}
              className="w-full"
            >
              Verify email
            </Button>
            <button
              type="button"
              onClick={() => void sendVerification(pendingEmail ?? '', true)}
              disabled={loading}
              className="w-full text-sm text-[var(--color-on-subtle)] hover:text-[var(--color-on-surface)] disabled:opacity-50"
            >
              Resend code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
